import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { cn } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import { DialogForm, CompactRichTextEditor } from '../../../shared/components/common';
import { useCreateInitiative } from '../hooks/useInitiativeQueries';
import { useModules } from '../hooks/useModuleQueries';
import { ModulePicker } from './ModulePicker';
import { InitiativePicker } from './InitiativePicker';
import { InitiativeOwnerPicker } from './InitiativeOwnerPicker';
import { PRIORITY_VALUES, priorityColors } from '../constants';
import type { SfInitiative } from '../types';

interface InitiativeQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (initiative: SfInitiative) => void;
  defaultParentId?: string | null;
  defaultModuleId?: string | null;
  defaultName?: string;
  /**
   * Navigate to the new initiative's detail page after creating (default true).
   * Set false when creating inline from a picker so the host form/dialog stays open.
   */
  navigateOnCreate?: boolean;
}

interface QuickCreateFormData {
  name: string;
  description: string;
  target_date: string;
  color: string;
  priority: number;
  module_id: string | null;
  parent_id: string | null;
  owner_user_id: string | null;
  owner_team_id: string | null;
}

export function InitiativeQuickCreateDialog({
  open,
  onClose,
  onCreated,
  defaultParentId,
  defaultModuleId,
  defaultName,
  navigateOnCreate = true,
}: InitiativeQuickCreateDialogProps) {
  const { t } = useTranslation(['softwareFactory', 'common']);
  const navigate = useNavigate();
  const createMutation = useCreateInitiative();

  const { data: modulesData } = useModules();
  const modules = modulesData?.items ?? [];

  const colorTouched = useRef(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    mode: 'onBlur',
    defaultValues: {
      name: defaultName ?? '',
      description: '',
      target_date: '',
      color: modules.find((m) => m.id === defaultModuleId)?.color ?? '#3B82F6',
      priority: 1,
      module_id: defaultModuleId ?? null,
      parent_id: defaultParentId ?? null,
      owner_user_id: null,
      owner_team_id: null,
    },
  });

  const selectedModuleId = useWatch({ control, name: 'module_id' });
  const ownerTeamId = useWatch({ control, name: 'owner_team_id' });

  // Auto-set color from module when module changes, unless user has manually picked a color
  useEffect(() => {
    if (colorTouched.current) return;
    const moduleColor = modules.find((m) => m.id === selectedModuleId)?.color;
    setValue('color', moduleColor ?? '#3B82F6');
  }, [selectedModuleId, modules, setValue]);

  useEffect(() => {
    if (open) {
      colorTouched.current = false;
      const initialColor = modules.find((m) => m.id === defaultModuleId)?.color ?? '#3B82F6';
      reset({
        name: defaultName ?? '',
        description: '',
        target_date: '',
        color: initialColor,
        priority: 1,
        module_id: defaultModuleId ?? null,
        parent_id: defaultParentId ?? null,
        owner_user_id: null,
        owner_team_id: null,
      });
    }
  }, [open, reset, defaultParentId, defaultModuleId, defaultName, modules]);

  const onSubmit = async (data: QuickCreateFormData) => {
    try {
      const newInitiative = await createMutation.mutateAsync({
        name: data.name,
        description: data.description || undefined,
        target_date: data.target_date || undefined,
        color: data.color || undefined,
        priority: data.priority,
        module_id: data.module_id || undefined,
        parent_id: data.parent_id || undefined,
        owner_user_id: data.owner_user_id || undefined,
        owner_team_id: data.owner_team_id || undefined,
      });
      onCreated?.(newInitiative);
      onClose();
      if (navigateOnCreate) {
        navigate(`/software-factory/initiatives/${newInitiative.id}`);
      }
    } catch {
      // The global mutation-error handler shows the specific translated toast.
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('softwareFactory:initiatives.create')}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={t('common:app.create')}
      isLoading={isSubmitting}
      maxWidth="sm"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name" required>{t('softwareFactory:initiatives.name')}</Label>
          <Controller
            name="name"
            control={control}
            rules={{ required: t('common:validation.required') }}
            render={({ field }) => (
              <Input
                {...field}
                id="name"
                autoFocus
                className={cn(errors.name && 'border-destructive')}
              />
            )}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label>{t('softwareFactory:initiatives.columns.priority')}</Label>
          <Controller
            name="priority"
            control={control}
            render={({ field }) => (
              <Select
                value={String(field.value)}
                onValueChange={(val) => field.onChange(Number(val))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_VALUES.map((val) => (
                    <SelectItem key={val} value={String(val)}>
                      <Badge className={priorityColors[val]}>
                        {t(`softwareFactory:priorities.${val}`)}
                      </Badge>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="space-y-2">
          <Label>{t('softwareFactory:initiatives.fieldDescription')}</Label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <CompactRichTextEditor
                value={field.value}
                onChange={field.onChange}
                minHeight={80}
              />
            )}
          />
        </div>

        <Controller
          name="module_id"
          control={control}
          render={({ field }) => (
            <ModulePicker
              value={field.value}
              onChange={field.onChange}
              label={t('softwareFactory:initiatives.module')}
            />
          )}
        />

        <Controller
          name="parent_id"
          control={control}
          render={({ field }) => (
            <InitiativePicker
              value={field.value}
              onChange={field.onChange}
              label={t('softwareFactory:initiatives.parentInitiative')}
            />
          )}
        />

        <Controller
          name="owner_user_id"
          control={control}
          render={({ field }) => (
            <InitiativeOwnerPicker
              ownerUserId={field.value}
              ownerTeamId={ownerTeamId}
              onChange={(owner) => {
                field.onChange(owner.owner_user_id);
                setValue('owner_team_id', owner.owner_team_id);
              }}
              label={t('softwareFactory:initiatives.owner')}
            />
          )}
        />

        <div className="space-y-2">
          <Label htmlFor="target_date">{t('softwareFactory:initiatives.targetDate')}</Label>
          <Controller
            name="target_date"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                id="target_date"
                type="date"
              />
            )}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="color">{t('softwareFactory:initiatives.color')}</Label>
          <Controller
            name="color"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                id="color"
                type="color"
                className="h-10 w-20 cursor-pointer p-1"
                onChange={(e) => {
                  colorTouched.current = true;
                  field.onChange(e);
                }}
              />
            )}
          />
        </div>
      </div>
    </DialogForm>
  );
}
